const DESKTOP_MQ = "(min-width: 1024px)";

class PersonalisationWrapper extends HTMLElement {
  /** @type {HTMLElement | null} */
  #track = null;
  /** @type {HTMLElement | null} */
  #indicator = null;
  /** @type {HTMLElement[]} */
  #tabs = [];
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  /** @type {MediaQueryList} */
  #mq = window.matchMedia(DESKTOP_MQ);
  /** @type {number} */
  #activeIndex = 0;

  connectedCallback() {
    this.#track = this.querySelector("[data-personalisation-tabs]");
    this.#indicator = this.querySelector("[data-personalisation-indicator]");
    this.#tabs = [...this.querySelectorAll("[data-switcher-item]")].filter(
      (el) => el instanceof HTMLElement
    );
    if (this.#tabs.length === 0) return;

    this.#tabs.forEach((tab, i) => {
      tab.addEventListener("click", () => this.#select(i));
      tab.addEventListener("keydown", (e) => this.#onTabKeydown(e, i));
    });
    this.#mq.addEventListener("change", this.#onMqChange);
    this.#resizeObserver = new ResizeObserver(() => this.#positionIndicator());
    if (this.#track) this.#resizeObserver.observe(this.#track);

    const initial = this.#tabs.findIndex((t) => t.getAttribute("aria-selected") === "true");
    this.#activeIndex = initial >= 0 ? initial : 0;
    this.#select(this.#activeIndex, false);
    requestAnimationFrame(() => this.#positionIndicator());
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#mq.removeEventListener("change", this.#onMqChange);
  }

  #onMqChange = () => {
    this.#updatePanels();
    this.#positionIndicator();
  };

  /**
   * @param {KeyboardEvent} e
   * @param {number} index
   */
  #onTabKeydown(e, index) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      this.#select(index);
      return;
    }
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft" && e.key !== "Home" && e.key !== "End")
      return;
    e.preventDefault();
    const n = this.#tabs.length;
    let next = index;
    if (e.key === "ArrowRight") next = (index + 1) % n;
    else if (e.key === "ArrowLeft") next = (index - 1 + n) % n;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = n - 1;
    this.#select(next);
    this.#tabs[next]?.focus();
  }

  #desktop() {
    return this.#mq.matches;
  }

  #updatePanels() {
    const panels = [...this.querySelectorAll("[data-switcher-panel]")];
    if (panels.length === 0) return;

    if (!this.#desktop()) {
      panels.forEach((p) => {
        p.removeAttribute("hidden");
        p.setAttribute("aria-hidden", "false");
      });
      return;
    }

    const id = this.#tabs[this.#activeIndex]?.dataset.switcherTargetId?.trim();
    /** If targets are blank or don’t match any row (e.g. IDs only set on rows), pair tabs to panels by order. */
    const useIndexFallback =
      !id ||
      !panels.some((p) => p.getAttribute("data-switcher-panel")?.trim() === id);

    panels.forEach((p, idx) => {
      const show = useIndexFallback
        ? idx === this.#activeIndex
        : p.getAttribute("data-switcher-panel")?.trim() === id;
      p.toggleAttribute("hidden", !show);
      p.setAttribute("aria-hidden", show ? "false" : "true");
    });
  }

  /**
   * @param {number} index
   * @param {boolean} animate
   */
  #select(index, animate = true) {
    if (index < 0 || index >= this.#tabs.length) return;

    this.#activeIndex = index;

    this.#tabs.forEach((tab, i) => {
      const active = i === index;
      tab.setAttribute("aria-selected", active ? "true" : "false");
      tab.tabIndex = active ? 0 : -1;
      tab.classList.toggle("opacity-20", !active);
      tab.classList.toggle("opacity-100", active);
    });

    this.#updatePanels();

    if (this.#indicator) {
      this.#indicator.classList.toggle("personalisation-tabs__indicator-rail--instant", !animate);
    }
    requestAnimationFrame(() => {
      this.#positionIndicator();
      if (this.#indicator && !animate) {
        requestAnimationFrame(() => {
          this.#indicator?.classList.remove("personalisation-tabs__indicator-rail--instant");
        });
      }
    });
  }

  #positionIndicator() {
    if (!this.#track || !this.#indicator) return;

    if (!this.#desktop()) {
      this.#indicator.style.opacity = "0";
      return;
    }

    const tab = this.#tabs[this.#activeIndex];
    if (!tab) return;

    this.#indicator.style.opacity = "1";
    const trackRect = this.#track.getBoundingClientRect();
    const tabRect = tab.getBoundingClientRect();

    if (trackRect.width <= 0 || tabRect.width <= 0) return;

    /** Tick and bar start at active tab’s left; bar runs to the track’s right edge. */
    let railLeft = tabRect.left - trackRect.left;
    let railWidth = trackRect.right - tabRect.left;
    railLeft = Math.min(Math.max(0, railLeft), trackRect.width);
    railWidth = Math.min(Math.max(0, railWidth), trackRect.width - railLeft);

    this.#indicator.style.setProperty("--rail-left", `${railLeft}px`);
    this.#indicator.style.setProperty("--rail-width", `${railWidth}px`);
  }
}

if (!customElements.get("personalisation-wrapper")) {
  customElements.define("personalisation-wrapper", PersonalisationWrapper);
}
